import { ChevronDown, ChevronUp, X } from 'lucide-react';

import { pluralize } from '@/react/common/string-utils';

import { Badge } from '@@/Badge';
import { Widget } from '@@/Widget';

import { ArtifactFile } from '../types';

type Props = {
  files: Array<ArtifactFile>;
  onReorder: (files: Array<ArtifactFile>) => void;
  onRemove: (index: number) => void;
};

export function ArtifactsSummary({ files, onReorder, onRemove }: Props) {
  return (
    <Widget>
      <Widget.Taskbar className="!mb-0">
        <span className="text-sm font-semibold text-gray-11 th-highcontrast:text-white th-dark:text-white">
          {files.length} Selected {pluralize(files.length, 'File')}
        </span>
      </Widget.Taskbar>
      <Widget.Body contentClassName="!p-0">
        {files.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-gray-6 th-highcontrast:text-gray-5 th-dark:text-gray-5">
            No files selected
          </p>
        ) : (
          files.map((file, index) => (
            <FileRow
              key={`${file.sourceId}-${file.path}`}
              file={file}
              isFirst={index === 0}
              isLast={index === files.length - 1}
              onMoveUp={() => onReorder(moveItem(files, index, index - 1))}
              onMoveDown={() => onReorder(moveItem(files, index, index + 1))}
              onRemove={() => onRemove(index)}
            />
          ))
        )}
      </Widget.Body>
    </Widget>
  );
}

interface FileRowProps {
  file: ArtifactFile;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

function FileRow({
  file,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onRemove,
}: FileRowProps) {
  return (
    <div className="flex items-center gap-2 border-b border-l-0 border-r-0 border-t-0 border-solid border-gray-4 bg-blue-1 px-3 py-2.5 th-highcontrast:border-white th-highcontrast:bg-gray-iron-10 th-dark:border-gray-7 th-dark:bg-blue-11">
      <Badge type="infoSecondary" shape="rect" size="sm" className="uppercase">
        File
      </Badge>
      <span
        className="min-w-0 flex-1 truncate font-mono text-xs text-gray-11 th-highcontrast:text-white th-dark:text-white"
        title={`/${file.path}`}
      >
        /{file.path}
      </span>
      <button
        type="button"
        onClick={onMoveUp}
        disabled={isFirst}
        className="flex shrink-0 items-center justify-center rounded border-0 bg-transparent p-0.5 text-gray-6 hover:text-gray-9 disabled:cursor-not-allowed disabled:opacity-40 th-highcontrast:text-white th-dark:text-gray-6"
        aria-label={`Move /${file.path} up`}
      >
        <ChevronUp size={12} />
      </button>
      <button
        type="button"
        onClick={onMoveDown}
        disabled={isLast}
        className="flex shrink-0 items-center justify-center rounded border-0 bg-transparent p-0.5 text-gray-6 hover:text-gray-9 disabled:cursor-not-allowed disabled:opacity-40 th-highcontrast:text-white th-dark:text-gray-6"
        aria-label={`Move /${file.path} down`}
      >
        <ChevronDown size={12} />
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="flex shrink-0 items-center justify-center rounded border-0 bg-transparent p-0.5 text-gray-6 hover:text-gray-9 th-highcontrast:text-white th-dark:text-gray-6"
        aria-label={`Remove /${file.path}`}
      >
        <X size={12} />
      </button>
    </div>
  );
}

function moveItem<T>(items: Array<T>, from: number, to: number): Array<T> {
  if (to < 0 || to >= items.length) {
    return items;
  }

  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
